/**
 * This file contains the contract types for actions.
 * It defines the data format for AI-suggested actions that clients can present to users.
 */

import { OptionData } from './common-types';

// Full action with type discriminator
export type AIAction = {
    [T in ActionType]: {
        type: T;
        data: GetActionData<T>;
    };
}[ActionType];

// Constants for action types
export const ACTION_TYPES = {
    OPTION_SELECTION: 'option_selection' as const,
    PROJECT_SELECTION: 'project_selection' as const,
    COLOR_SELECTION: 'color_selection' as const,
    COMPANY_LOGO_UPLOAD: 'company_logo_upload' as const,
    NAVIGATION: 'navigation' as const,
    FILE_UPLOAD: 'file_upload' as const
};

// Supported action types
export type ActionType = (typeof ACTION_TYPES)[keyof typeof ACTION_TYPES];

/**
 * Action data types
 */

export type NoData = Record<string, never>;

export type OptionSelectionData = {
    options: OptionData[];
};

export type NavigationData = {
    destination: string;
};

// ----------------
// Action utility types
// ----------------

// Maps action types to their data structures
export type ActionDataMap = {
    [ACTION_TYPES.OPTION_SELECTION]: OptionSelectionData;
    [ACTION_TYPES.PROJECT_SELECTION]: NoData;
    [ACTION_TYPES.COLOR_SELECTION]: NoData;
    [ACTION_TYPES.COMPANY_LOGO_UPLOAD]: NoData;
    [ACTION_TYPES.NAVIGATION]: NavigationData;
};

// Returns the data type for an action type
export type GetActionData<T extends ActionType> = T extends keyof ActionDataMap
    ? ActionDataMap[T]
    : {};
